// src/components/RhinoVOIPVoicemails.jsx
import React, { useState, useEffect } from 'react';
import { RhinoSwitch } from 'rhinostyle';
import { useGetVoicemailsByExtensionQuery, useMarkVoicemailStatusMutation } from '../services/VoipService';
import EmptyMessage from './EmptyMessage';
import useIsMobile from '../hooks/useIsMobile';
import '../assets/styles/rhinovoip-voicemails.less';
import { useVoip } from '../voip/VoipProvider';
import ringGroupIcon from '../assets/images/voip/ring-group-icon.svg';
import makeCallIcon from '../assets/images/voip/make-call-icon.png';
import RhinoVoipVoicemailPlayer from './RhinoVoipVoicemailPlayer';

// ------------------ Component ------------------

const RhinoVOIPVoicemails = ({ autoPlayVoicemailId, onAutoPlayConsumed }) => {
  const { myExtension, call: placeCall, callPhase } = useVoip();
  const [includeRinggroups, setIncludeRinggroups] = useState(true);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetVoicemailsByExtensionQuery(includeRinggroups);
  const [markVoicemailStatus] = useMarkVoicemailStatusMutation();

  const isVoicemailUnread = (vm) => {
    const s = vm?.message_status;
    return s == null || s === '' || s === 'unread';
  };

  const markVoicemailRead = (vm) => {
    if (!vm?.voicemail_message_uuid) return;
    if (!isVoicemailUnread(vm)) return;
    markVoicemailStatus({ voicemailMessageId: vm.voicemail_message_uuid, status: 'read' });
  };

  // Refetch on mount and then every 10 seconds
  useEffect(() => {
    refetch(); // on component load

    const intervalId = setInterval(() => {
      refetch();
    }, 10_000);

    return () => {
      clearInterval(intervalId);
    };
  }, [refetch]);

  // Keep your existing "call ended" refresh (slight delay)
  useEffect(() => {
    let t;

    if (callPhase === 'ended') {
      t = setTimeout(() => {
        refetch();
      }, 500);
    }

    return () => {
      if (t) clearTimeout(t);
    };
  }, [callPhase, refetch]);

  const [pendingAutoPlay, setPendingAutoPlay] = useState(null);

  useEffect(() => {
    if (autoPlayVoicemailId) {
      setPendingAutoPlay(autoPlayVoicemailId);
      onAutoPlayConsumed();
    }
  }, [autoPlayVoicemailId, onAutoPlayConsumed]);

  const [expandedRow, setExpandedRow] = useState(null);

  const handleRowToggle = (call) => {
    const willExpand = expandedRow !== call.voicemail_message_uuid;
    setExpandedRow(willExpand ? call.voicemail_message_uuid : null);
    if (willExpand) markVoicemailRead(call);
  };
  const [confirmCall, setConfirmCall] = useState(null);
  const isMobile = useIsMobile(768);

  if (isLoading) return <div>Loading voicemails...</div>;

  const calls = Array.isArray(data?.objects) ? data.objects : [];

  if (error && calls.length === 0) {
    return <div className="vm-empty-state">Unable to load voicemails. Please try again.</div>;
  }

  function extractCallTarget(call) {
    return call.caller_id_number;
  }

  function handleMobileClick(call) {
    const target = extractCallTarget(call);
    if (!target) return;

    try {
      placeCall(target);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Mobile call start failed:', err);
    }
  }

  function formatEpochDate(epoch) {
    if (epoch == null || epoch === '') return '-';

    const n = Number(epoch);
    if (!Number.isFinite(n)) return '-';

    // support epoch in seconds (10 digits) or milliseconds (13 digits)
    const ms = n < 1e12 ? n * 1000 : n;

    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '-';

    return `${d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })} ${d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })}`;
  }

  function formatPhone(value) {
    if (!value) return value;

    const digits = value.replace(/\D/g, '');

    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    return value;
  }

  function formatDuration(sec) {
    const n = Number(sec);
    if (!Number.isFinite(n) || n <= 0) return '00:00';

    const total = Math.floor(n);
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  function displayWithRingGroupIcon(label, number = null, showIcon = true) {
    const text = number ? `${label} (${number})` : label;

    if (!showIcon) return text;

    return (
      <span className="ringgroup-label">
        <img src={ringGroupIcon} alt="Ring Group" className="ringgroup-icon" /> {text}
      </span>
    );
  }

  function truncateTranscription(text, maxLen) {
    if (!text) return 'No Transcription Available';
    if (text.length <= maxLen) return text;

    return `${text.slice(0, maxLen)}...`;
  }

  // ------------------ Caller / Receiver Logic ------------------

  function callFrom(call) {
    const num = call.caller_id_number;
    const displayName = call.caller_contact_name || call.caller_id_name;

    if (num === myExtension) return `Me (${num})`;

    if (displayName && displayName !== num) {
      return `${displayName} - ${formatPhone(num)}`;
    }
    return formatPhone(num) || 'Unknown';
  }

  function openCallConfirm(call) {
    const target = extractCallTarget(call);
    if (!target) return;
    setConfirmCall({
      label: call.caller_contact_name || call.caller_id_name || formatPhone(target) || target,
      targetNumber: target,
    });
  }

  function displayParty(call) {
    return callFrom(call);
  }

  function displayCallTarget(call) {
    const rgName = call.ring_group_name;

    if (rgName) {
      return displayWithRingGroupIcon(rgName, call.ring_group_extension, true);
    }

    if (isMobile) {
      return '';
    }

    return 'Direct';
  }

  function getCallConfirmDialogtext() {
    const { label, targetNumber } = confirmCall;

    if (label === targetNumber) {
      return `Call ${formatPhone(targetNumber)}?`;
    } else {
      return `Call ${label} at ${formatPhone(targetNumber)}?`;
    }
  }

  // function markVmAs(voicemailMessageUuid, status) {

  // }

  // function downloadVm(voicemailMessageUuid) {

  // }

  return (
    <div className="vm-container">
      <div
        style={{
          display: 'flex',
          gap: '6px',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <RhinoSwitch
          isChecked={includeRinggroups}
          onChange={() => setIncludeRinggroups((prev) => !prev)}
          name="includeRinggroupsVm"
        />
        Include Ring Groups
      </div>

      {calls.length === 0 && (
        <EmptyMessage section="Voicemails" />
      )}

      {calls.length > 0 && isMobile && (
        // -------- MOBILE VIEW --------
        <div className="vm-mobile-list">
          {calls.map((call) => {
            const isExpanded = expandedRow === call.voicemail_message_uuid;
            const transcription = call.message_transcription;
            const unread = isVoicemailUnread(call);

            return (
              <div
                key={call.voicemail_message_uuid}
                className={`vm-mobile-card${isExpanded ? ' vm-mobile-expanded' : ''}${unread ? ' vm-row-unread' : ''}`}
                onClick={() => handleRowToggle(call)}
              >
                <div className="vm-header-row">
                  <span className="vm-name">
                    {unread && <span className="vm-unread-dot" aria-label="Unread" />}
                    &nbsp;&nbsp;{displayParty(call)}
                    <br />
                    {displayCallTarget(call)}
                  </span>

                  <span className="vm-ts">
                    {formatEpochDate(call.created_epoch)}
                  </span>
                </div>

                <div className="vm-meta">
                  {formatDuration(call.message_length)}
                  <span className="vm-transcription-preview" style={{ marginLeft: '8px' }}>
                    {truncateTranscription(transcription, 40)}
                  </span>
                </div>

                {isExpanded && (
                  <div className="vm-transcription-full" style={{ marginTop: '8px' }}>
                    {transcription || 'No Transcription Available'}
                    <div style={{ marginTop: '8px' }}>
                      <RhinoVoipVoicemailPlayer
                        voicemailMessageId={call.voicemail_message_uuid}
                        autoPlay={call.voicemail_message_uuid === pendingAutoPlay}
                        onAutoPlayStarted={() => setPendingAutoPlay(null)}
                        onPlay={() => markVoicemailRead(call)}
                      />
                    </div>
                    <button
                      type="button"
                      className="vm-mobile-callback-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMobileClick(call);
                      }}
                    >
                      Call Back
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {calls.length > 0 && !isMobile && (
        // -------- DESKTOP TABLE VIEW --------
        <div className="vm-table-container">
          <table className="vm-table">
            <thead>
              <tr>
                <th>Party</th>
                <th>Direct/Group</th>
                <th>Audio</th>
                <th>Received</th>
                <th>Duration</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {calls.map((call) => {
                const isExpanded = expandedRow === call.voicemail_message_uuid;
                const transcription = call.message_transcription;
                const unread = isVoicemailUnread(call);

                return (
                  <React.Fragment key={call.voicemail_message_uuid}>
                    <tr
                      className={`vm-row-clickable${isExpanded ? ' vm-row-expanded' : ''}${unread ? ' vm-row-unread' : ''}`}
                      onClick={() => handleRowToggle(call)}
                    >
                      <td>
                        <div>
                          {unread && <span className="vm-unread-dot" aria-label="Unread" />}
                          {displayParty(call)}
                        </div>
                        <div className="vm-transcription-preview">
                          {truncateTranscription(transcription, 60)}
                        </div>
                      </td>
                      <td>{displayCallTarget(call)}</td>
                      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                      <td onClick={(e) => e.stopPropagation()} role="cell">
                        <RhinoVoipVoicemailPlayer
                          voicemailMessageId={call.voicemail_message_uuid}
                          autoPlay={call.voicemail_message_uuid === pendingAutoPlay}
                          onAutoPlayStarted={() => setPendingAutoPlay(null)}
                          onPlay={() => markVoicemailRead(call)}
                        />
                      </td>
                      <td>{formatEpochDate(call.created_epoch)}</td>
                      <td>{formatDuration(call.message_length)}</td>
                      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                      <td onClick={(e) => e.stopPropagation()} role="cell">
                        <button
                          type="button"
                          className="cdr-action-icon"
                          onClick={() => openCallConfirm(call)}
                          title="Call Back"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          <img src={makeCallIcon} alt="Call Back" width="24" height="24" />
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="vm-transcription-row">
                        <td colSpan={6}>
                          <div className="vm-transcription-full">
                            {transcription || 'No Transcription Available'}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirmCall && (
        <div className="vm-modal-overlay">
          <div className="vm-modal">
            <div className="vm-modal-title">Confirm Call</div>
            <div className="vm-modal-body">
              {getCallConfirmDialogtext(confirmCall)}
            </div>

            <div className="vm-modal-actions">
              <button
                type="button"
                className="vm-modal-cancel"
                onClick={() => setConfirmCall(null)}
              >
                Cancel
              </button>

              <button
                type="button"
                className="vm-modal-confirm"
                onClick={() => {
                  placeCall(confirmCall.targetNumber);
                  setConfirmCall(null);
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RhinoVOIPVoicemails;
